import {
  Circle,
  Line,
  Path,
  Rect,
  StyleSheet,
  Svg,
  Text,
  View,
} from "@react-pdf/renderer";
import { parseDate, parseTime } from "helpers/date";

const MARKER_COLOR = "#10b981";
const MARKER_ERROR_COLOR = "#ef4444";

function EnvelopeMarker({ color = MARKER_COLOR }: { color?: string }) {
  return (
    <Svg width={18} height={18} viewBox="0 0 24 24">
      <Circle
        cx={12}
        cy={12}
        r={11}
        stroke={color}
        strokeWidth={1.5}
        fill="#ffffff"
      />
      <Rect
        x={6}
        y={9}
        width={12}
        height={7}
        rx={1}
        ry={1}
        stroke={color}
        strokeWidth={1.2}
        fill="none"
      />
      <Path
        d="M6.2 9.3 L12 13 L17.8 9.3"
        stroke={color}
        strokeWidth={1.2}
        fill="none"
        strokeLineCap="round"
      />
    </Svg>
  );
}

function CommentMarker({ color = MARKER_COLOR }: { color?: string }) {
  return (
    <Svg width={18} height={18} viewBox="0 0 24 24">
      <Circle
        cx={12}
        cy={12}
        r={11}
        stroke={color}
        strokeWidth={1.5}
        fill="#ffffff"
      />
      <Path
        d="M7 9 a1.5 1.5 0 0 1 1.5 -1.5 H15.5 a1.5 1.5 0 0 1 1.5 1.5 V13 a1.5 1.5 0 0 1 -1.5 1.5 H10 L7.5 17 V14.5 a1 1 0 0 1 -0.5 -0.5 z"
        stroke={color}
        strokeWidth={1.2}
        fill="none"
        strokeLineCap="round"
      />
    </Svg>
  );
}

function TimesCircleMarker({ color = MARKER_ERROR_COLOR }: { color?: string }) {
  return (
    <Svg width={18} height={18} viewBox="0 0 24 24">
      <Circle
        cx={12}
        cy={12}
        r={11}
        stroke={color}
        strokeWidth={1.5}
        fill="#ffffff"
      />
      <Line
        x1={9}
        y1={9}
        x2={15}
        y2={15}
        stroke={color}
        strokeWidth={1.6}
        strokeLineCap="round"
      />
      <Line
        x1={15}
        y1={9}
        x2={9}
        y2={15}
        stroke={color}
        strokeWidth={1.6}
        strokeLineCap="round"
      />
    </Svg>
  );
}

function renderMarker(type: string) {
  switch (type) {
    case "EmailSent":
    case "EmailResent":
      return <EnvelopeMarker />;
    case "TextMessageSent":
    case "TextMessageResent":
      return <CommentMarker />;
    case "TextMessageFailed":
      return <TimesCircleMarker />;
    default:
      return <EnvelopeMarker />;
  }
}

export interface IOrderHistoryEntry {
  id?: string;
  type: string;
  createdAt: string;
  description?: string;
}

const TYPE_LABELS: Record<string, string> = {
  EmailSent: "Email Sent",
  TextMessageSent: "Text Message Sent",
  EmailResent: "Email Resent",
  TextMessageResent: "Text Message Resent",
  TextMessageFailed: "Text Message Failed",
};

const DESCRIPTION_LABELS: Record<string, string> = {
  Message1: "Message 1",
  Message2: "Message 2",
  Message3: "Message 3",
  Receipt: "Receipt",
};

export const COMMUNICATION_TYPES: Set<string> = new Set(
  Object.keys(TYPE_LABELS)
);

export const hasCommunicationHistory = (
  orderHistories: IOrderHistoryEntry[]
): boolean => orderHistories.some((h) => COMMUNICATION_TYPES.has(h.type));

const local = StyleSheet.create({
  container: {
    marginHorizontal: 50,
    marginVertical: 12,
    padding: "10px 15px",
    borderRadius: 10,
    borderWidth: 1,
    borderColor: "#d9d9d9",
    backgroundColor: "#ffffff",
  },
  title: {
    fontSize: 14,
    marginBottom: 12,
  },
  empty: {
    fontSize: 11,
    color: "#666",
  },
  eventRow: {
    flexDirection: "row",
    alignItems: "stretch",
  },
  timelineCol: {
    width: 24,
    alignItems: "center",
    position: "relative",
  },
  connector: {
    position: "absolute",
    top: 0,
    bottom: 0,
    left: 11,
    width: 2,
    backgroundColor: "#d9d9d9",
    zIndex: 0,
  },
  connectorFromMarker: {
    position: "absolute",
    top: 11,
    bottom: 0,
    left: 11,
    width: 2,
    backgroundColor: "#d9d9d9",
    zIndex: 0,
  },
  markerWrapper: {
    marginTop: 2,
    position: "relative",
    zIndex: 2,
    width: 18,
    height: 18,
    borderRadius: 9,
    backgroundColor: "#ffffff",
  },
  eventContent: {
    flex: 1,
    marginLeft: 8,
    paddingBottom: 14,
  },
  eventType: {
    fontSize: 11,
    fontFamily: "Helvetica-Bold",
    color: "#0d0d0d",
    marginBottom: 2,
  },
  eventMeta: {
    fontSize: 10,
    color: "#666",
  },
});

interface IOrderHistorySectionProps {
  orderHistories: IOrderHistoryEntry[];
}

export function OrderHistorySection({
  orderHistories,
}: IOrderHistorySectionProps) {
  const communications = [...orderHistories]
    .filter((item) => COMMUNICATION_TYPES.has(item.type))
    .sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );

  return (
    <View style={local.container}>
      <Text style={local.title}>Communication History</Text>

      {communications.length === 0 ? (
        <Text style={local.empty}>No communication history</Text>
      ) : (
        communications.map((item, index) => {
          const isFirst = index === 0;
          const isLast = index === communications.length - 1;
          const dateText = `${parseDate(
            item.createdAt,
            false,
            true
          )} ${parseTime(item.createdAt, true)}`;
          const descriptionText = item.description
            ? DESCRIPTION_LABELS[item.description] ?? item.description
            : null;
          return (
            <View
              key={item.id || `${item.type}-${item.createdAt}-${index}`}
              style={local.eventRow}
            >
              <View style={local.timelineCol}>
                {!isLast && (
                  <View
                    style={
                      isFirst ? local.connectorFromMarker : local.connector
                    }
                  />
                )}
                <View style={local.markerWrapper}>
                  {renderMarker(item.type)}
                </View>
              </View>
              <View style={local.eventContent}>
                <Text style={local.eventType}>
                  {TYPE_LABELS[item.type] ?? item.type}
                </Text>
                <Text style={local.eventMeta}>
                  {descriptionText
                    ? `${dateText} — ${descriptionText}`
                    : dateText}
                </Text>
              </View>
            </View>
          );
        })
      )}
    </View>
  );
}
